import PropTypes from "prop-types";
import styled from "styled-components";
import { FaPrint, FaShare, FaClock } from "react-icons/fa";
import ContentLayout from "../../../layout/ContentLayout";
import { H2, H3, Paragraph } from "../../../styles/AppStyles";
import { LINKS, href } from "../../../config/links";

const CalloutBox = styled.div`
  margin: 1.5rem 0;
  padding: 1.25rem;
  border-left: 5px solid ${({ $type, theme }) => {
    if ($type === "critical") return "#dc2626";
    if ($type === "warning") return "#f59e0b";
    if ($type === "do") return "#10b981";
    if ($type === "dont") return "#ef4444";
    if ($type === "tip") return "#6366f1";
    return theme.iconColor;
  }};
  background-color: ${({ $type, theme }) => {
    if ($type === "critical") return "rgba(220, 38, 38, 0.08)";
    if ($type === "warning") return "rgba(245, 158, 11, 0.08)";
    if ($type === "do") return "rgba(16, 185, 129, 0.08)";
    if ($type === "dont") return "rgba(239, 68, 68, 0.08)";
    if ($type === "tip") return "rgba(99, 102, 241, 0.08)";
    return "rgba(99, 102, 241, 0.08)";
  }};
  border-radius: 6px;
  color: ${({ theme }) => theme.text};

  strong {
    display: block;
    margin-bottom: 0.5rem;
    font-size: 1.1rem;
    color: ${({ $type }) => {
      if ($type === "critical") return "#dc2626";
      if ($type === "warning") return "#f59e0b";
      if ($type === "do") return "#10b981";
      if ($type === "dont") return "#ef4444";
      if ($type === "tip") return "#6366f1";
      return "#6366f1";
    }};
  }

  ul {
    margin: 0.75rem 0 0 0;
    padding-left: 1.5rem;

    li {
      margin-bottom: 0.5rem;
      line-height: 1.6;
    }
  }
`;

const ChecklistSection = styled.div`
  margin: 1.5rem 0;
  padding: 1.25rem;
  background-color: ${({ theme }) => theme.background};
  border: 1px solid ${({ theme }) => theme.iconColor};
  border-radius: 6px;
  color: ${({ theme }) => theme.text};

  h3 {
    margin-top: 0;
  }
`;

const ChecklistItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;

  input[type="checkbox"] {
    margin-top: 0.35rem;
    flex-shrink: 0;
    width: 1.1rem;
    height: 1.1rem;
    cursor: pointer;
  }

  label {
    flex: 1;
    cursor: pointer;
    line-height: 1.6;
  }
`;

const PhaseBox = styled.div`
  margin: 1.5rem 0;
  padding: 1.5rem;
  background-color: ${({ theme }) => theme.background};
  border: 2px solid ${({ theme }) => theme.iconColor};
  border-radius: 8px;
  color: ${({ theme }) => theme.text};

  h3 {
    margin-top: 0;
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .phase-time {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9rem;
    color: ${({ theme }) => theme.iconColor};
    margin-bottom: 1rem;
    font-weight: 600;
  }

  ol {
    margin: 0.75rem 0 0 0;
    padding-left: 1.5rem;

    li {
      margin-bottom: 0.75rem;
      line-height: 1.6;
    }
  }
`;

const DecisionTree = styled.div`
  margin: 1.5rem 0;
  padding: 1.5rem;
  background-color: ${({ theme }) => theme.background};
  border: 2px dashed ${({ theme }) => theme.iconColor};
  border-radius: 8px;
  color: ${({ theme }) => theme.text};

  .branch {
    padding: 1rem;
    margin: 0.75rem 0;
    border-radius: 6px;
    background-color: ${({ theme }) =>
      theme.isdarkmode ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.03)"};
  }

  .branch-question {
    font-weight: 700;
    margin-bottom: 0.5rem;
  }

  .branch-yes {
    border-left: 4px solid #10b981;
  }

  .branch-no {
    border-left: 4px solid #ef4444;
  }
`;

const ScenarioBox = styled.div`
  margin: 1.5rem 0;
  padding: 1.25rem;
  background-color: ${({ theme }) => theme.background};
  border: 2px dashed ${({ theme }) => theme.iconColor};
  border-radius: 6px;
  color: ${({ theme }) => theme.text};

  h4 {
    margin-top: 0;
    color: ${({ theme }) => theme.iconColor};
  }

  .scenario-text {
    font-style: italic;
    margin: 0.75rem 0;
    padding: 0.75rem;
    background-color: ${({ theme }) =>
      theme.isdarkmode ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.05)"};
    border-radius: 4px;
  }

  .response {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid ${({ theme }) => theme.iconColor};

    strong {
      color: #10b981;
      display: block;
      margin-bottom: 0.5rem;
    }

    ul {
      margin: 0;
      padding-left: 1.5rem;

      li {
        margin-bottom: 0.5rem;
      }
    }
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 1rem;
  margin: 2rem 0;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    flex-direction: column;

    button,
    a {
      width: 100%;
    }
  }
`;

const Button = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.875rem 1.5rem;
  background-color: ${({ theme }) => theme.iconColor};
  color: ${({ theme }) => theme.background};
  text-decoration: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  cursor: pointer;
  border: none;

  &:hover {
    opacity: 0.9;
    transform: translateY(-2px);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const RansomwareResponseGuide = ({ istouch, isdarkmode }) => {
  const canonicalUrl = href(LINKS.QUICK_GUIDES.RANSOMWARE_RESPONSE);

  // const heroImageSrc = "/images/guides/ransomware-response.webp";

  return (
    <ContentLayout
        istouch={istouch}
        isdarkmode={isdarkmode}
        displayTitle="Help! My Files Are Locked: Quick Ransomware Response"
        seoTitle="Ransomware Response Guide: What to Do First | CSFSafe"
        canonicalUrl={canonicalUrl}
        description="Your files are encrypted and a ransom note appeared. Follow this quick guide for the first hour, containment steps, and whether to pay or recover from backup."
      ogTitle="Ransomware Response Guide: What to Do First | CSFSafe"
      ogDescription="A ransom note just appeared. Know the first-hour checklist, containment steps, and the pay-vs-recover decision tree for SMBs."
      // ogImage={heroImageSrc}
      // heroImageSrc={heroImageSrc}
      heroImageAlt="Ransomware response and file recovery"
      publishedLabel="Quick Reference Guide — July 22, 2026"
      showCTA={true}
    >
      <Paragraph>
        A ransom note on your screen or files that suddenly won't open is one of
        the most frightening moments in business. What you do in the first hour
        determines how much damage spreads and how quickly you recover. This
        guide walks through immediate actions, containment, the pay-vs-recover
        decision, and what to avoid.
      </Paragraph>

      <H2>What Is Ransomware?</H2>

      <Paragraph>
        Ransomware is malicious software that encrypts your files—or locks you
        out of your systems entirely—and demands payment (usually
        cryptocurrency) for the decryption key. Modern ransomware often also
        steals data before encrypting it, then threatens to publish or sell it
        even if you pay ("double extortion"). Attackers typically gain access
        through phishing emails, exposed remote access (RDP), unpatched
        software, or compromised credentials.
      </Paragraph>

      <H3>Recognizing the Signs</H3>

      <CalloutBox $type="critical">
        <strong>Signs You're Dealing With Ransomware</strong>
        <ul>
          <li>Files suddenly have new extensions you don't recognize</li>
          <li>A ransom note (text file, desktop wallpaper, or pop-up) appears</li>
          <li>You can no longer open documents, spreadsheets, or databases</li>
          <li>Systems are unusually slow or shared drives become inaccessible</li>
          <li>Security software alerts about encryption activity or disabled backups</li>
        </ul>
      </CalloutBox>

      <H2>Immediate Actions: The First Hour</H2>

      <PhaseBox>
        <h3>Do This First</h3>
        <div className="phase-time">
          <FaClock /> 0–60 minutes
        </div>
        <ol>
          <li>
            <strong>Disconnect, don't shut down:</strong> Unplug the network
            cable or disable WiFi on affected machines immediately to stop the
            encryption from spreading. Avoid powering off—shutting down can
            destroy evidence and sometimes memory-resident decryption keys.
          </li>
          <li>
            <strong>Isolate shared drives and servers:</strong> Disconnect
            network shares, mapped drives, and backup servers from the network.
            Ransomware actively hunts for connected storage.
          </li>
          <li>
            <strong>Alert your IT/security team immediately:</strong> Don't
            try to fix it alone. Escalate to whoever owns your incident
            response process, or your managed security provider.
          </li>
          <li>
            <strong>Photograph the ransom note:</strong> Take a photo with your
            phone (don't screenshot from the infected machine) for evidence and
            for law enforcement.
          </li>
          <li>
            <strong>Identify what's affected:</strong> Quickly determine which
            systems, servers, and shares show signs of encryption. This scopes
            the incident.
          </li>
          <li>
            <strong>Do not pay yet and do not delete anything:</strong> Don't
            interact with the ransom note, don't pay immediately, and don't
            delete encrypted files or the note itself—you'll need them.
          </li>
        </ol>
      </PhaseBox>

      <ChecklistSection>
        <h3>30-Minute Checklist</h3>
        <ChecklistItem>
          <input type="checkbox" id="item1" />
          <label htmlFor="item1">
            Disconnect infected devices from the network (WiFi and Ethernet)
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item2" />
          <label htmlFor="item2">
            Disconnect or power off network-attached storage and backup
            appliances before they're encrypted too
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item3" />
          <label htmlFor="item3">
            Notify IT lead, management, and your incident response plan owner
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item4" />
          <label htmlFor="item4">
            Photograph the ransom note and record the ransomware "brand" name
            if shown
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item5" />
          <label htmlFor="item5">
            List every affected system, server, and shared drive
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item6" />
          <label htmlFor="item6">
            Check whether clean, offline backups exist and are unaffected
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item7" />
          <label htmlFor="item7">
            Change passwords for administrative accounts from a known-clean
            device
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item8" />
          <label htmlFor="item8">
            Contact your cyber insurance carrier if you have a policy—many
            require notification before you take further action
          </label>
        </ChecklistItem>
      </ChecklistSection>

      <H2>Containment Steps</H2>

      <PhaseBox>
        <h3>Stop the Spread</h3>
        <div className="phase-time">
          <FaClock /> Hours 1–24
        </div>
        <ol>
          <li>
            <strong>Segment the network:</strong> If you have managed
            switches, isolate affected VLANs or subnets to prevent lateral
            movement to unaffected systems.
          </li>
          <li>
            <strong>Reset credentials broadly:</strong> Assume the attacker
            had access to credentials. Reset passwords for domain admin,
            service accounts, VPN, and email—starting with the most
            privileged accounts.
          </li>
          <li>
            <strong>Preserve evidence:</strong> Don't wipe or reimage systems
            yet. A forensic team will want to examine logs, the ransom note,
            and sample encrypted files to identify the ransomware family.
          </li>
          <li>
            <strong>Check for data exfiltration:</strong> Review outbound
            network traffic and logs for large data transfers in the days
            before the attack was discovered—this affects notification
            obligations even if you recover files.
          </li>
          <li>
            <strong>Engage a ransomware-experienced IR firm:</strong> If you
            don't have in-house expertise, bring in specialists who negotiate,
            investigate, and rebuild ransomware cases regularly.
          </li>
        </ol>
      </PhaseBox>

      <H2>When to Involve Authorities</H2>

      <CalloutBox $type="tip">
        <strong>Report It—Law Enforcement Can Help</strong>
        <ul>
          <li>
            Report to the FBI's Internet Crime Complaint Center (ic3.gov) as
            soon as possible—some ransomware families have known decryption
            tools law enforcement can point you to
          </li>
          <li>
            Contact your local FBI field office for active, high-impact
            incidents
          </li>
          <li>
            Notify CISA (cisa.gov) for cyber threats affecting critical
            infrastructure or widespread ransomware campaigns
          </li>
          <li>
            If personal or customer data was likely stolen, involve legal
            counsel early—there may be state or federal notification deadlines
          </li>
          <li>
            Do not assume paying the ransom is illegal, but be aware some
            ransomware groups are sanctioned entities—your IR firm or legal
            counsel can advise on OFAC compliance before any payment
          </li>
        </ul>
      </CalloutBox>

      <H2>Decision Tree: Pay or Recover From Backup?</H2>

      <DecisionTree>
        <div className="branch">
          <div className="branch-question">
            Do you have clean, tested, offline (or immutable) backups of the
            affected data?
          </div>
          <div className="branch branch-yes">
            <strong>Yes →</strong> Restore from backup. Verify backups are not
            infected before restoring, rebuild affected systems from a known
            clean state, and only reconnect to the network after confirming
            the attacker's access has been removed.
          </div>
          <div className="branch branch-no">
            <strong>No →</strong> Continue below.
          </div>
        </div>

        <div className="branch">
          <div className="branch-question">
            Is the encrypted data critical to operations, and is there no
            other way to reconstruct it (from paper records, source systems,
            or partial exports)?
          </div>
          <div className="branch branch-yes">
            <strong>Yes →</strong> Consult your IR firm, legal counsel, and
            insurance carrier about negotiation and payment. Never negotiate
            or pay directly without expert guidance—there is no guarantee
            attackers will provide a working decryption key.
          </div>
          <div className="branch branch-no">
            <strong>No →</strong> Rebuild systems from scratch, accept the
            data loss for non-critical files, and focus resources on hardening
            against future attacks instead of paying.
          </div>
        </div>

        <div className="branch">
          <div className="branch-question">
            Has a free decryptor been published for this ransomware family?
          </div>
          <div className="branch branch-yes">
            <strong>Yes →</strong> Check "No More Ransom" (nomoreransom.org)
            and your IR firm's threat intelligence before paying anything.
          </div>
          <div className="branch branch-no">
            <strong>No →</strong> Weigh cost of payment (with no guarantee)
            against cost of rebuild and lost productivity, guided by your IR
            firm and insurer.
          </div>
        </div>
      </DecisionTree>

      <CalloutBox $type="warning">
        <strong>Why Paying Is Risky</strong>
        <ul>
          <li>No guarantee you'll receive a working decryption key</li>
          <li>Attackers may leave backdoors even after "resolving" the incident</li>
          <li>Paying can mark you as a target willing to pay again</li>
          <li>Some payments may violate sanctions laws depending on the group</li>
          <li>
            Even with decryption, stolen data may still be leaked or sold
          </li>
        </ul>
      </CalloutBox>

      <H2>Backup Recovery</H2>

      <H3>Restore Systems the Right Way</H3>

      <ChecklistSection>
        <h3>Restoring Safely</h3>
        <ChecklistItem>
          <input type="checkbox" id="item9" />
          <label htmlFor="item9">
            Confirm the backup predates the infection and is scanned clean
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item10" />
          <label htmlFor="item10">
            Rebuild or reimage infected machines rather than restoring files
            onto a potentially compromised OS
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item11" />
          <label htmlFor="item11">
            Patch the vulnerability or close the access point that let
            attackers in before reconnecting to the network
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item12" />
          <label htmlFor="item12">
            Reset all credentials again after rebuild, including service
            accounts and API keys
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item13" />
          <label htmlFor="item13">
            Monitor restored systems closely for 30+ days for signs of
            reinfection
          </label>
        </ChecklistItem>
      </ChecklistSection>

      <H2>What NOT to Do</H2>

      <CalloutBox $type="dont">
        <strong>Avoid These Mistakes</strong>
        <ul>
          <li>
            Don't pay the ransom without consulting legal counsel, your
            insurer, and an experienced IR firm
          </li>
          <li>
            Don't restart or reimage systems before evidence is preserved—you
            may need it for insurance, forensics, or law enforcement
          </li>
          <li>
            Don't reconnect "cleaned" systems to the network without
            confirming the attacker's access is fully removed—reinfection
            within days is common
          </li>
          <li>
            Don't try to negotiate with attackers yourself—experienced
            negotiators know the tactics and can verify decryption capability
            first
          </li>
          <li>
            Don't assume backups are safe just because they exist—verify they
            weren't encrypted or deleted too
          </li>
          <li>
            Don't stay silent—notify employees, customers, and regulators as
            required once scope is understood
          </li>
        </ul>
      </CalloutBox>

      <H2>Contact Template</H2>

      <CalloutBox $type="tip">
        <strong>Internal Ransomware Alert (Fill in and Save Now)</strong>
        <ul>
          <li>
            <strong>Incident Response Lead:</strong> [Name, Phone, Email]
          </li>
          <li>
            <strong>IT/Security Contact:</strong> [Name, Phone, Email]
          </li>
          <li>
            <strong>Cyber Insurance Carrier &amp; Policy #:</strong> [Fill in]
          </li>
          <li>
            <strong>External IR Firm (24/7 line):</strong> [Company, Phone]
          </li>
          <li>
            <strong>Legal Counsel:</strong> [Name, Phone, Email]
          </li>
          <li>
            <strong>FBI IC3 Report:</strong> ic3.gov
          </li>
        </ul>
      </CalloutBox>

      <H2>Real-World Scenario</H2>

      <ScenarioBox>
        <h4>A Manufacturer Locked Out on a Friday Afternoon</h4>
        <div className="scenario-text">
          At 4:45 PM on a Friday, an employee reports they can't open any files
          on the shared drive. A ransom note appears demanding payment in
          Bitcoin within 72 hours. IT finds the same extension on dozens of
          files across three servers.
        </div>
        <div className="response">
          <strong>What They Did Right:</strong>
          <ul>
            <li>
              Immediately disconnected the affected servers and any machine
              showing encrypted files
            </li>
            <li>Left the untouched backup server powered off and unplugged</li>
            <li>
              Called their cyber insurance carrier that evening, who connected
              them with an approved IR firm within hours
            </li>
            <li>
              Confirmed weekly offline backups were unaffected and restored
              from a backup 36 hours old
            </li>
            <li>
              Lost less than two days of data and avoided paying any ransom
            </li>
          </ul>
        </div>
      </ScenarioBox>

      <H2>Related Resources</H2>
      <ul>
        <li>
          <a href={href("/articles/ransomware-small-business")}>
            Ransomware and the Small Business
          </a>
        </li>
        <li>
          <a href={href(LINKS.THREAT_TYPE.RANSOMWARE)}>
            Ransomware Threat Type: Full Article
          </a>
        </li>
        <li>
          <a href={href(LINKS.QUICK_GUIDES.INCIDENT_RESPONSE)}>
            Quick Incident Response Checklist
          </a>
        </li>
        <li>
          <a href={href(LINKS.CYBERSECURITY_FRAMEWORK.BCDR_BASICS)}>
            BCDR Basics: Backup and Disaster Recovery
          </a>
        </li>
        <li>
          <a
            href={href(
              LINKS.CYBERSECURITY_FRAMEWORK.DATA_BREACH_RESPONSE_AND_COMPLIANCE
            )}
          >
            Data Breach Response and Compliance
          </a>
        </li>
      </ul>

      <ButtonRow data-print="hide">
        <Button
          as="button"
          type="button"
          onClick={() => window.print()}
          style={{ fontFamily: "inherit" }}
        >
          <FaPrint /> Print or save as PDF
        </Button>
        <Button
          href="https://www.facebook.com/sharer/sharer.php?u=https://csfsafe.com/resources/quick-guides/ransomware-response"
          target="_blank"
          rel="noopener noreferrer"
        >
          <FaShare /> Share Guide
        </Button>
      </ButtonRow>
      </ContentLayout>
  );
};

RansomwareResponseGuide.propTypes = {
  istouch: PropTypes.bool.isRequired,
  isdarkmode: PropTypes.bool.isRequired,
};

export default RansomwareResponseGuide;
